/**
 * @file recordDataExtraction.ts
 * @description Utilities for extracting structured display data from raw Fulcrum records.
 *
 * This module handles three main concerns:
 *  1. **Schema indexing** – Build a flat field-key → metadata map from a nested Fulcrum form schema.
 *  2. **Field extraction** – Pull typed field values (text, choice, photo) out of a record using that index.
 *  3. **Basic record assembly** – Compose a minimal display object (id, name, thumbnail, geometry) for list views.
 *
 * Exported surface:
 *  - `extractFormSchema`  – Build a field-key map from a Fulcrum form definition.
 *  - `getRefinedData`     – Map an array of raw Fulcrum records to minimal display objects.
 *
 * @module fulcrumAdapterUtils/recordDataExtraction
 */

import { constructFulcrumPhotoUrl } from "../SharedAdapterUtils";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Supported Fulcrum field types used for typed value extraction. */
type FulcrumFieldType = "PhotoField" | "TextField" | "ChoiceField" | string;

/**
 * Metadata stored for each field in the schema index, keyed by the field's
 * Fulcrum `key` (short alphanumeric ID, e.g. `"ea1a"`).
 */
interface FieldMeta {
  label: string;
  type: FulcrumFieldType;
  data_name: string | undefined;
  description: string | undefined;
  required: boolean | undefined;
  /** Whether multiple selections are allowed (ChoiceField). */
  multiple: boolean | undefined;
  /** Available choices for ChoiceField. */
  choices: any[] | undefined;
}

/**
 * A single extracted field value with its display label and Fulcrum type.
 * Used internally to build the filtered schema for name / thumbnail lookup.
 */
interface ExtractedFieldValue {
  type: FulcrumFieldType;
  label: string;
  /** Resolved typed value – string for text, string[] for choices, photo-array for photos. */
  value: any;
}

/**
 * Minimal display representation of a Fulcrum record used in list / grid views.
 * This is intentionally lean – full record detail is fetched separately on demand.
 */
interface BasicRecordInfo {
  id: string;
  name: string | undefined;
  description: string | undefined;
  geometry: any;
  thumbnail: string | null;
}

// ─── 1. Schema Indexing ───────────────────────────────────────────────────────

/**
 * Recursively traverse a Fulcrum form's element tree and build a flat index
 * of `fieldKey → FieldMeta`.
 *
 * Fulcrum forms can nest fields inside Section and Repeatable containers.
 * This function walks the entire tree so every field – at any depth – is reachable
 * by its short key without knowing its nesting path.
 *
 * @param elements - Array of Fulcrum form elements (may be nested).
 * @param fieldMap - Accumulator; pass an empty object on the first call.
 * @returns The same `fieldMap` object, mutated and returned for convenience.
 */
const buildFieldMap = (elements: Record<string, any>[], fieldMap: Record<string, FieldMeta> = {}): Record<string, FieldMeta> => {
  if (!Array.isArray(elements)) return fieldMap;

  for (const element of elements) {
    // Index the current element by its Fulcrum key
    fieldMap[element.key] = {
      label: element.label,
      type: element.type,
      data_name: element.data_name,
      description: element.description,
      required: element.required,
      multiple: element.multiple,
      choices: element.choices
    };

    // Recurse into container types (Section, Repeatable, etc.)
    if (Array.isArray(element.elements)) {
      buildFieldMap(element.elements, fieldMap);
    }
  }

  return fieldMap;
};

/**
 * Build a flat `{ [fieldKey]: FieldMeta }` index from a raw Fulcrum form object.
 *
 * Usage:
 * ```ts
 * const schema = extractFormSchema(apiResponse.form);
 * // schema["ea1a"] → { label: "Site Name", type: "TextField", ... }
 * ```
 *
 * @param form - The `form` object returned by the Fulcrum Forms API (must contain `elements`).
 * @returns Flat record mapping each field key to its metadata.
 */
export const extractFormSchema = (form: Record<string, any>): Record<string, FieldMeta> => {
  return buildFieldMap(form.elements);
};

// ─── 2. Typed Field Extraction ────────────────────────────────────────────────

/**
 * Extract fields of the requested types from a single Fulcrum record, resolving
 * the field key to its human-readable label and normalising the value per type:
 *
 * | Type         | Resolved value                                  |
 * |:-------------|:------------------------------------------------|
 * | `TextField`  | Raw string value                                |
 * | `ChoiceField`| `choice_values` + `other_values` merged to `string[]` |
 * | `PhotoField` | Raw photo array from `form_values`              |
 *
 * Only fields **present in `form_values`** and **matching one of `types`** are included.
 *
 * @param record  - A single raw Fulcrum record (must have `form_values`).
 * @param schema  - The field schema index produced by `extractFormSchema`.
 * @param types   - Whitelist of Fulcrum field types to include.
 * @returns Array of `ExtractedFieldValue` objects ready for display or further processing.
 */
const getRecordFieldsByType = (
  record: Record<string, any>,
  schema: Record<string, FieldMeta>,
  types: FulcrumFieldType[]
): ExtractedFieldValue[] => {
  const formValues = record?.form_values ?? {};
  const results: ExtractedFieldValue[] = [];

  for (const [key, meta] of Object.entries(schema)) {
    if (!types.includes(meta.type)) continue;

    const rawValue = formValues[key];

    switch (meta.type) {
      case "PhotoField":
        results.push({ type: meta.type, label: meta.label, value: rawValue });
        break;

      case "TextField":
        results.push({ type: meta.type, label: meta.label, value: rawValue });
        break;

      case "ChoiceField": {
        // Merge selected choices and free-text "other" entries into a single array
        const choiceValues: string[] = rawValue?.choice_values ?? [];
        const otherValues: string[] = rawValue?.other_values ?? [];
        results.push({ type: meta.type, label: meta.label, value: [...choiceValues, ...otherValues] });
        break;
      }
    }
  }

  return results;
};

// ─── 3. Fuzzy Label Matching ──────────────────────────────────────────────────

/**
 * Normalise a label string for fuzzy comparison:
 * lower-case → underscores for spaces → strip non-alphanumeric → collapse/trim underscores.
 *
 * @example
 * normalizeLabel("Site Name") // "site_name"
 * normalizeLabel("weed_species (other)") // "weed_species_other"
 */
const normalizeLabel = (str: string): string =>
  str
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

/**
 * Generate a small set of normalised key variations to account for
 * common Fulcrum naming patterns (e.g. "species_other" suffix).
 */
const generateKeyVariations = (key: string): string[] => {
  const norm = normalizeLabel(key);
  return [
    norm,
    `${norm}_other`, // Fulcrum "other" variant
    norm.replace(/_/g, "") // no-separator variant
  ];
};

/**
 * Check whether a field label fuzzy-matches a search key,
 * using both exact and prefix-based comparisons.
 */
const labelsMatch = (fieldLabel: string, searchKey: string): boolean => {
  const normalizedField = normalizeLabel(fieldLabel);
  const variations = generateKeyVariations(searchKey);

  return variations.some(
    (v) =>
      normalizedField === v ||
      normalizedField.replace(/_/g, "") === v.replace(/_/g, "") ||
      normalizedField.startsWith(v) ||
      v.startsWith(normalizedField)
  );
};

/**
 * Search an array of extracted field values for the first entry whose label
 * fuzzy-matches any key in `keyList` and whose value is truthy.
 *
 * The search iterates `keyList` in order (highest-priority keys first), so callers
 * can control resolution precedence by ordering the list appropriately.
 *
 * @param fieldValues - Extracted field values from `getRecordFieldsByType`.
 * @param keyList     - Ordered list of field names / IDs to search for.
 * @returns The first matching `ExtractedFieldValue`, or `undefined` if none found.
 */
const findMatchingFieldValue = (fieldValues: ExtractedFieldValue[], keyList: string[]): ExtractedFieldValue | undefined => {
  for (const key of keyList) {
    for (const field of fieldValues) {
      if (labelsMatch(field.label, key) && field.value) {
        return field;
      }
    }
  }
  return undefined;
};

// ─── 4. Basic Info Extraction ─────────────────────────────────────────────────

/**
 * Resolve the display name for a record from its extracted field values.
 *
 * Resolution order:
 *  1. Field matching one of the names in `endpointConfig.fieldMapping.name`.
 *  2. First `TextField` found in the extracted values (last-resort fallback).
 *
 * Field type handling:
 *  - `TextField`  → raw string value.
 *  - `ChoiceField` → choices joined with `", "`.
 *
 * @param fieldValues    - Output of `getRecordFieldsByType` for this record.
 * @param endpointConfig - Endpoint configuration that may carry a `fieldMapping.name` hint.
 * @returns Display name string, or `undefined` if no suitable field found.
 */
const extractName = (fieldValues: ExtractedFieldValue[], endpointConfig: Record<string, any>): string | undefined => {
  const priorityKeys: string[] = endpointConfig?.fieldMapping?.name ?? [];
  const match = findMatchingFieldValue(fieldValues, priorityKeys);

  if (match?.type === "TextField") return match.value;
  if (match?.type === "ChoiceField") return (match.value as string[]).join(", ");

  // No priority match – fall back to the first available text field
  if (!match) {
    return fieldValues.find((f) => f.type === "TextField")?.value?.toString();
  }

  return match?.value?.toString();
};

const extractDescription = (fieldValues: ExtractedFieldValue[], endpointConfig: Record<string, any>): string | undefined => {
  const priorityKeys: string[] = endpointConfig?.fieldMapping?.description ?? [];
  const match = findMatchingFieldValue(fieldValues, priorityKeys);

  if (match?.type === "TextField") return match.value;
  if (match?.type === "ChoiceField") return (match.value as string[]).join(", ");

  // No priority match – fall back to the first available text field
  if (!match) {
    return fieldValues.find((f) => f.type === "TextField")?.value?.toString();
  }

  return match?.value?.toString();
};

/**
 * Build a thumbnail URL from the first photo found in `fieldValues`.
 *
 * Fulcrum photo fields contain an array of `{ photo_id, caption }` objects.
 * We use `constructFulcrumPhotoUrl` with `thumbnail = true` to get the
 * smaller preview image.
 *
 * @param fieldValues - Output of `getRecordFieldsByType` (must include PhotoField results).
 * @param recordId    - The Fulcrum record ID used to construct the authenticated URL.
 * @returns Thumbnail URL string, or `null` if no photo is present.
 */
const extractThumbnail = (fieldValues: ExtractedFieldValue[], recordId: string): string | null => {
  const firstPhotoField = fieldValues.find((f) => f.type === "PhotoField");
  const firstPhotoId: string | undefined = firstPhotoField?.value?.[0]?.photo_id;

  if (!firstPhotoId || !recordId) return null;

  // `true` → request thumbnail-size image
  return constructFulcrumPhotoUrl(firstPhotoId, recordId, true);
};

/**
 * Assemble the minimal display object for a single Fulcrum record.
 *
 * Internally this:
 *  1. Extracts only `TextField` and `PhotoField` values (sufficient for name + thumbnail).
 *  2. Resolves the display name from those fields.
 *  3. Picks the first photo as a thumbnail.
 *  4. Passes geometry through unchanged.
 *
 * @param record         - Raw Fulcrum record from the paginated JSON API.
 * @param schema         - Schema index built by `extractFormSchema`.
 * @param endpointConfig - Endpoint-level config (used for `fieldMapping.name` hints).
 * @returns A `BasicRecordInfo` object suitable for list / grid rendering.
 */
const extractBasicInfo = (
  record: Record<string, any>,
  schema: Record<string, FieldMeta>,
  endpointConfig: Record<string, any>
): BasicRecordInfo => {
  // Only pull text and photo fields – we don't need full field data for a summary view
  const typedFields = getRecordFieldsByType(record, schema, ["PhotoField", "TextField", "ChoiceField"]);

  return {
    id: record.id,
    name: extractName(typedFields, endpointConfig),
    description: extractDescription(typedFields, endpointConfig),
    geometry: record?.geometry ?? null,
    thumbnail: extractThumbnail(typedFields, record?.id)
  };
};

// ─── 5. Public API ────────────────────────────────────────────────────────────

/**
 * Transform an array of raw Fulcrum records into minimal display objects
 * (`BasicRecordInfo`) for rendering in list or grid views.
 *
 * This is the primary export used by `FulcrumAdapter.fetchEndpointData` to
 * convert the paginated JSON API response into something the DataGrid can render
 * without fetching full record detail for every row.
 *
 * @param data           - Array of raw Fulcrum records (from paginated JSON API).
 * @param schema         - Field schema index built by `extractFormSchema`.
 * @param endpointConfig - Endpoint-level config (used for name field mapping hints).
 * @returns Array of `BasicRecordInfo` objects.
 */
export const getRefinedData = (
  data: Record<string, any>[],
  schema: Record<string, FieldMeta>,
  endpointConfig: Record<string, any>
): BasicRecordInfo[] => {
  return data.map((record) => extractBasicInfo(record, schema, endpointConfig));
};
